import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactGA from 'react-ga4';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClose } from '@fortawesome/free-solid-svg-icons';
import { validateWac } from '../../redux/actions/campaign/landing';
import { getProfileOverview } from '../../redux/actions/profile/profile';
import { resetSaveData, resetSaveResult } from '../../redux/actions/saveData/saveData';
import { EXTERNAL_IN_PROGRESS, EXTERNAL_SUBMITTED, INTERNAL_IN_PROGRESS, NEW_CREATION, RESET_GET_PROFILE_OVERVIEW, RESET_LOOKUPS, RESET_PROPERTIES } from '../../redux/actionTypes/actionsTypes';
import { ProfileOverviewState } from '../../redux/reducers/getProfile/getProfileOverviewReducer';
import { PropertiesState } from '../../redux/reducers/getProperties/propertiesReducer';
import Button from '../../components/common/Button/Button';
import Input from '../../components/common/Input/Input';
import ErrorMessages from '../../components/common/ErrorMessages/ErrorMessages';
import LoadingSpinner from '../../components/common/LoadingSpinner/LoadingSpinner';
import Pagination from '../../components/common/Pagination/Pagination';
import Search from '../../components/common/Search/Search';
import LandingBanner from '../../components/LandingBanner/LandingBanner';
import Footer from '../../layouts/Footer/Footer';
import Header from '../../layouts/Header/Header';
import PropertyObject from '../../types/property/PropertyObject';
import { FormError, useFormErrorContext } from '../../utils/context/FormErrorContext';
import { defaultNumberPerPage } from '../../assets/data/pagination/pagination';
import { formatRoll } from '../../utils/formatter';
import { setActiveMenuItem } from '../../redux/actions/menu/menu';
import { initialMenuItem } from '../../redux/reducers/getMenu/getMenuReducer';
import ToolTip from "../../components/common/ToolTip/ToolTip";
import "./Landing.css";
import Radio from '../../components/common/Radio/Radio';
import RollSearch from '../../components/common/RollSearch/RollSearch';
import { selectMenu, selectProfileGetOverview, selectProperties } from '../../redux/selectors/exportSelectorPaths';
import reportMenuDuration from '../../utils/GA4EventHelpers/reportMenuDuration';

export default function Landing(): JSX.Element {
    const isValidateWacEnabled = (process.env.REACT_APP_IS_VALIDATE_WAC_ENABLED === "1" && !sessionStorage.getItem("wac")) ? true : false
    const lang = localStorage.getItem("prefLang") ?? "en"
    const { t } = useTranslation('landing');
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const profileOverview: ProfileOverviewState = useSelector(selectProfileGetOverview);
    const propertiesState: PropertiesState = useSelector(selectProperties);
    const menuState = useSelector(selectMenu);
    const [isProfileReqSent, setIsProfileReqSent] = useState(false);
    const [currentProperty, setCurrentProperty] = useState<PropertyObject>();
    const campaignYear = (process.env.REACT_APP_ACTIVE_CAMPAIGN_YEAR ? Number(process.env.REACT_APP_ACTIVE_CAMPAIGN_YEAR) : null);
    const [showValidateWacDialog, setShowValidateWacDialog] = useState<boolean>(isValidateWacEnabled)
    const { addError, clearErrors, errors } = useFormErrorContext()
    const [formErrors, setFormErrors] = useState<FormError[]>([])
    const [searchInput, setSearchInput] = useState("")
    const gettingProperties = useRef(false)
    const [acknowledgedBy, setAcknowledgedBy] = useState<string>("")
    const [wac, setWac] = useState(sessionStorage.getItem("wac") ? sessionStorage.getItem("wac") : "");

    useEffect(() => {
        clearErrors()
        dispatch(setActiveMenuItem(initialMenuItem))
        reportMenuDuration(menuState, "Home")
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    // Pagination related states and methods
    const [pageData, setPageData] = useState<Array<PropertyObject> | null>(propertiesState.profiles);
    const [totalPages, setTotalPages] = useState(0);
    const [itemOffset, setItemOffset] = useState(0);
    const [itemsPerPage, setItemsPerPage] = useState(defaultNumberPerPage);
    const [page, setPage] = useState<number | undefined>(undefined)

    useEffect(() => {
        const endOffset = itemOffset + itemsPerPage;
        setPageData(propertiesState.profiles.slice(itemOffset, endOffset));
        setTotalPages(Math.ceil(propertiesState.profiles.length / itemsPerPage));
    }, [itemOffset, itemsPerPage, propertiesState.profiles]);

    useEffect(() => {
        if (isProfileReqSent && !profileOverview.loading && profileOverview.found && campaignYear) {
            setIsProfileReqSent(false);
            if (!profileOverview.profile.current_campaign) {
                toast.error("No campaign found for roll");
            }
            else {
                let path = `/${campaignYear}/${currentProperty?.rollNumber}/${currentProperty?.profileId}/${currentProperty?.profileTypeCode}/`;
                switch (currentProperty?.statusCode as string) {
                    case NEW_CREATION:
                        path += "start";
                        break;
                    case EXTERNAL_IN_PROGRESS:
                        path += "continue";
                        break;
                    case EXTERNAL_SUBMITTED:
                        path += "overview";
                        break;
                    default:
                        path += "overview";
                }
                navigate(path);
                sessionStorage.setItem("rollNumber", currentProperty?.rollNumber as string);
                sessionStorage.setItem("profileId", currentProperty?.profileId as string);
                sessionStorage.setItem("propertyCode", currentProperty?.propertyCode as string);

                ReactGA.gtag("set", "user_properties", {
                    property_code: currentProperty?.propertyCode as string
                })
                dispatch(resetSaveData());
                dispatch(resetSaveResult());
            }
        }
        else if (isProfileReqSent && !profileOverview.loading && !profileOverview.found) {
            setIsProfileReqSent(false);
            toast.error("Failed to retrieve profile details");
        }
    }, [campaignYear, currentProperty, dispatch, isProfileReqSent, navigate, profileOverview, wac])

    const chooseProperty = (property: PropertyObject) => {
        setCurrentProperty(property);
        if (profileOverview && profileOverview.profile.profile_type_code !== property.profileTypeCode) {
            dispatch({ type: RESET_LOOKUPS });
        }
        if (property.profileId === "0") {
            toast.error("Invalid profile, please verify details");
        } else {
            dispatch({ type: RESET_GET_PROFILE_OVERVIEW })
            dispatch(getProfileOverview(property.profileId, wac as string));
            setIsProfileReqSent(true);
        }
    }

    const handleItemsPerPage = (itemCount: number) => {
        setItemsPerPage(itemCount);
    }

    const handlePageClick = ({ selected }: { selected: number }) => {
        if (pageData) {
            const newOffset = (selected * itemsPerPage) % propertiesState.profiles.length;
            setItemOffset(newOffset);
        }
    };

    const handlePageData = useCallback((data: PropertyObject[]) => {
        let offset = itemOffset
        if (offset >= data.length) {
            offset = data.length - itemsPerPage
            setPage(Math.ceil(data.length / itemsPerPage) - 1)
        } else {
            setPage(undefined)
        }
        const endOffset = offset + itemsPerPage;
        setPageData(data.slice(offset, endOffset));
        setTotalPages(Math.ceil(data.length / itemsPerPage));
    }, [itemOffset, itemsPerPage]);

    const filterRolls = useCallback((searchString: string) => {
        if (searchString) {

            if (searchString.startsWith("-")) {
                searchString = searchString.replace("-", "")
            }
            setSearchInput(searchString)
            const regex = /^[0-9\- ]+$/
            if (regex.test(searchString)) {
                searchString = searchString.replaceAll("-", "")?.replaceAll(" ", "")
            }
            const filteredData = propertiesState.profiles.filter(currentProperty => currentProperty.rollNumber.includes(searchString) || currentProperty.profileAddress?.includes(searchString.toUpperCase()));
            handlePageData(filteredData);
        }
        else {
            setSearchInput("")
            handlePageData(propertiesState.profiles);
        }
    }, [handlePageData, propertiesState])

    useEffect(() => {
        if (propertiesState.loading === false) {
            if (propertiesState.found) {
                filterRolls(searchInput);
                setShowValidateWacDialog(false)
                dispatch({ type: RESET_GET_PROFILE_OVERVIEW })
                sessionStorage.setItem("wac", wac as string);
                gettingProperties.current = false
            } else {
                if (isValidateWacEnabled) {
                    setShowValidateWacDialog(true)
                    if (propertiesState.message && gettingProperties.current) {
                        addError({
                            primaryKey: "validatewac",
                            level: "error",
                            message: propertiesState.message,
                            messageFr: propertiesState.message,
                        })
                        gettingProperties.current = false
                    }
                } else {
                    if (!gettingProperties.current) {
                        gettingProperties.current = true
                    }
                }
            }
        }
    }, [addError, dispatch, filterRolls, isValidateWacEnabled, propertiesState, searchInput, wac])

    const submitValidateWac = (event: React.FormEvent<HTMLFormElement>) => {
        dispatch({ type: RESET_PROPERTIES })
        clearErrors();

        event.preventDefault()
        const data: FormData = new FormData(event.target as HTMLFormElement);
        const formValues = Object.fromEntries(data.entries()) as Record<string, string>
        setWac(formValues['wac']);
        dispatch(validateWac({ ...formValues, "lastAcknowledgedBy": acknowledgedBy }))
        gettingProperties.current = true
    }

    const closeValidateWacDialog = () => {
        window.location.href = `${process.env.REACT_APP_AMP_URL}/#/auth/home/reg-user`;
    }
    useEffect(() => {
        setFormErrors(errors)
    }, [errors])

    const formatAddress = (address: string) => {
        address = address?.trim()
        if (address?.length > 17) {
            return address.substring(0, 17)?.trim() + "..."
        } else {
            return address
        }
    }

    return (
        <>
            {
                showValidateWacDialog ?
                    <>
                        <div className="modal-bg">
                            <div className="modal-container">
                                <div className="modal-header">
                                    <span className="modal-title">{t("validate-wac.header")}</span>
                                    <div className="close-btn">
                                        <Button forceEnabled={true} type="cancel" ariaLabel="cancel" onClick={() => closeValidateWacDialog()}>
                                            <FontAwesomeIcon icon={faClose} />
                                        </Button>
                                    </div>
                                </div>
                                <div className="modal-body">
                                    <LoadingSpinner loading={propertiesState.loading}>
                                        <div className='validate-wac-modal-body'>
                                            <ErrorMessages errors={formErrors} showHeader={false} />
                                            <p></p>

                                            <form onSubmit={(e) => submitValidateWac(e)}>
                                                <fieldset className='validate-wac-fieldset noborder-fieldset' aria-label="login-roll-wac-screen">
                                                    <legend className='invisible-legend'><span>Roll/WAC screen</span></legend>
                                                    <div className='validate-wac-field-group'>
                                                        <label>{t("validate-wac.acknowledgedBy")}</label>
                                                        <table className='validate-wac-options-table'>
                                                            <tbody>
                                                                <tr>
                                                                    <td><div className='validate-wac-radio'>
                                                                        <Radio value="owner"
                                                                            onClick={() => setAcknowledgedBy("owner")}
                                                                            checked={"owner" === acknowledgedBy}
                                                                            id="owner" />
                                                                    </div></td>
                                                                    <td>
                                                                        {t("validate-wac.option-owner")}
                                                                    </td>
                                                                </tr>
                                                                <tr>
                                                                    <td><div className='validate-wac-radio'>
                                                                        <Radio value="employee"
                                                                            onClick={() => setAcknowledgedBy("employee")}
                                                                            checked={"employee" === acknowledgedBy}
                                                                            id="employee" />
                                                                    </div></td>
                                                                    <td>
                                                                        {t("validate-wac.option-employee")}
                                                                    </td>
                                                                </tr>
                                                                <tr>
                                                                    <td><div className='validate-wac-radio'>
                                                                        <Radio value="licensed"
                                                                            onClick={() => setAcknowledgedBy("licensed")}
                                                                            checked={"licensed" === acknowledgedBy}
                                                                            id="licensed" />
                                                                    </div></td>
                                                                    <td>
                                                                        {t("validate-wac.option-licensed", { activeCampaignYear: process.env.REACT_APP_ACTIVE_CAMPAIGN_YEAR })}
                                                                    </td>
                                                                </tr>
                                                                <tr>
                                                                    <td><div className='validate-wac-radio'>
                                                                        <Radio value="completedform"
                                                                            onClick={() => setAcknowledgedBy("completedform")}
                                                                            checked={"completedform" === acknowledgedBy}
                                                                            id="completed-form" />
                                                                    </div></td>
                                                                    <td>
                                                                        {t("validate-wac.option-completedForm1", { activeCampaignYear: process.env.REACT_APP_ACTIVE_CAMPAIGN_YEAR })}
                                                                        <a href={t("validate-wac.option-completedForm-link")} target='_blank' rel="noreferrer">{t("validate-wac.option-completedForm2", { activeCampaignYear: process.env.REACT_APP_ACTIVE_CAMPAIGN_YEAR })}</a>
                                                                        {t("validate-wac.option-completedForm3")}
                                                                    </td>
                                                                </tr>
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                    <div className='validate-wac-field-group'>
                                                        <label htmlFor="rollNumber">{t("validate-wac.roll-number")}</label>
                                                        <RollSearch
                                                            name={'rollNumber'}
                                                            id={'rollNumber'}
                                                            maxLength={15}
                                                            disabled={!acknowledgedBy}
                                                        />                                                    </div>
                                                    <div className='validate-wac-field-group'>
                                                        <label htmlFor='wac'>{t("validate-wac.wac")}</label>
                                                        <Input name={'wac'} id={'wac'} type={'text'} disabled={!acknowledgedBy} />
                                                    </div>
                                                </fieldset>
                                                <div className='button-group justify-content-center'>
                                                    <Button type='submit' text={t("validate-wac.login")} disabled={!acknowledgedBy} />
                                                </div>
                                            </form>
                                            <p></p>
                                        </div>
                                    </LoadingSpinner>
                                </div>
                            </div>
                        </div>
                    </>
                    :
                    <>
                        <Header showAutoSave={false} isInternalUser={false} showRollNumber={false} setSearchInput={setSearchInput} />
                        <LandingBanner />
                        <div className='landing-body-container'>
                            <div className='landing-body'>
                                <h1 className='landing-heading' aria-label='roll list below'>
                                    {t("welcome-heading")}
                                </h1>
                                <div className='search-roll-container'>
                                    <Search showInputByDefault={true} handleSearch={filterRolls} searchInput={searchInput} setSearchInput={setSearchInput} />
                                </div>
                                <LoadingSpinner loading={profileOverview.loading || propertiesState.loading}>
                                    <div className="profile-list-container">
                                        <table className='profile-table' cellSpacing="0" cellPadding="0">
                                            <thead>
                                                <tr className='profile-table-heading-container'>
                                                    <th className='roll-width'>{t("title.roll-number")}</th>
                                                    <th className='address-width'>{t("title.address")}</th>
                                                    <th className='type-width'>{t("title.profile-type")}</th>
                                                    <th className='status-width'>{t("title.status")}</th>
                                                </tr>
                                            </thead>
                                            <tbody className='profile-table-body-container'>
                                                {pageData && (pageData).map((property, index) => {
                                                    return (
                                                        <tr key={`property-${index}`} onClick={() => chooseProperty(property)}>
                                                            <td className="roll-width">{formatRoll(property.rollNumber)}</td>
                                                            <td className="address-width">
                                                                <ToolTip contents={formatAddress(property.profileAddress)} placement="top">
                                                                    {property.profileAddress?.length > 260 ? property.profileAddress?.slice(0, 260) + "..." : property.profileAddress}
                                                                </ToolTip>
                                                            </td>
                                                            <td className="type-width">{lang === "fr" && property.propertyTypeNameFr ? property.propertyTypeNameFr : property.propertyTypeName}</td>
                                                            <td className="status-width">
                                                                <span className={`status`}>
                                                                    {
                                                                        [NEW_CREATION, EXTERNAL_IN_PROGRESS, EXTERNAL_SUBMITTED, INTERNAL_IN_PROGRESS].includes(property.statusCode) ?
                                                                            (property.statusCode === INTERNAL_IN_PROGRESS ? t("status." + EXTERNAL_SUBMITTED) : t("status." + property.statusCode))
                                                                            :
                                                                            t("status.N/A")
                                                                    }
                                                                </span>
                                                            </td>
                                                        </tr>
                                                    )
                                                })}
                                            </tbody>
                                        </table>

                                        {totalPages > 1 &&
                                            <Pagination
                                                breakLabel='...'
                                                nextLabel='Next'
                                                previousLabel='Previous'
                                                onPageChange={handlePageClick}
                                                itemsPerPage={itemsPerPage}
                                                totalPages={totalPages}
                                                setItemsPerPage={handleItemsPerPage}
                                                forcePage={page}
                                                pageRangeDisplayed={7} />
                                        }
                                    </div>
                                </LoadingSpinner>
                            </div>
                        </div>
                        <Footer />
                    </>
            }
        </>
    );
}
